import React, { useState, useEffect } from 'react';
import {
  Search, Filter, User, Activity, Calendar, Eye,
  MessageSquare, FileText, DollarSign, Bot, LogIn,
  LogOut, RefreshCw, AlertCircle, TrendingUp, Users
} from 'lucide-react';
import api from '../services/api';
import { formatDateTime } from '../utils/dateUtils';

function PortalActivityAudit() {
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionTypes, setActionTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({
    action: '',
    contact_id: '',
    start_date: '',
    end_date: '',
    search: '',
    success: ''
  });
  const [summaryDays, setSummaryDays] = useState(7);

  // Engagement analytics state
  const [engagement, setEngagement] = useState(null);
  const [engagementDays, setEngagementDays] = useState(30);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [ghostClients, setGhostClients] = useState([]);
  const [ghostDays, setGhostDays] = useState(30);

  useEffect(() => {
    loadActionTypes();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      loadLogs();
    } else if (activeTab === 'engagement') {
      loadEngagement();
    } else {
      loadSummary();
    }
  }, [activeTab, offset, summaryDays, engagementDays]);

  const loadEngagement = async () => {
    setEngagementLoading(true);
    try {
      const [dashResp, ghostResp] = await Promise.all([
        api.get(`/client-portal/admin/analytics/dashboard?days=${engagementDays}`),
        api.get(`/client-portal/admin/analytics/ghost-clients?inactive_days=${ghostDays}`),
      ]);
      setEngagement(dashResp.data);
      setGhostClients(ghostResp.data.ghost_clients || []);
    } catch (err) {
      console.error('Failed to load engagement data:', err);
    } finally {
      setEngagementLoading(false);
    }
  };

  const loadActionTypes = async () => {
    try {
      const response = await api.get('/audit/portal/actions');
      setActionTypes(response.data.actions);
    } catch (err) {
      console.error('Failed to load action types:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', '50');
      params.append('offset', offset.toString());

      if (filters.action) params.append('action', filters.action);
      if (filters.contact_id) params.append('contact_id', filters.contact_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.search) params.append('search', filters.search);
      if (filters.success !== '') params.append('success', filters.success);

      const response = await api.get(`/audit/portal/logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
      setError('');
    } catch (err) {
      setError('Failed to load portal activity logs: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/audit/portal/summary?days=${summaryDays}`);
      setSummary(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load summary: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setOffset(0);
    loadLogs();
  };

  const handleReset = () => {
    setFilters({
      action: '',
      contact_id: '',
      start_date: '',
      end_date: '',
      search: '',
      success: ''
    });
    setOffset(0);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login':
        return <LogIn className="w-4 h-4 text-green-500" />;
      case 'login_failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'logout':
        return <LogOut className="w-4 h-4 text-gray-500" />;
      case 'case_detail_view':
      case 'case_list_view':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'document_upload':
      case 'document_download':
      case 'document_list_view':
        return <FileText className="w-4 h-4 text-cyan-500" />;
      case 'message_send':
      case 'message_list_view':
        return <MessageSquare className="w-4 h-4 text-teal-500" />;
      case 'invoice_detail_view':
      case 'invoice_list_view':
        return <DollarSign className="w-4 h-4 text-yellow-500" />;
      case 'payment_initiated':
        return <DollarSign className="w-4 h-4 text-green-500" />;
      case 'ai_chat':
        return <Bot className="w-4 h-4 text-indigo-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionLabel = (action) => {
    return action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const renderLogsTab = () => (
    <>
      {/* Filters */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-virgo-teal-400" />
          <h2 className="text-lg font-semibold text-white">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-virgo-teal-500" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by name, email, IP..."
                className="w-full pl-9 pr-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Action Type
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            >
              <option value="">All Actions</option>
              {actionTypes.map(at => (
                <option key={at.value} value={at.value}>{at.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Status
            </label>
            <select
              value={filters.success}
              onChange={(e) => handleFilterChange('success', e.target.value)}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            >
              <option value="">All</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSearch}
            className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors"
          >
            <Search className="w-4 h-4" />
            Apply Filters
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 border border-virgo-teal-700 text-virgo-teal-200 rounded-lg hover:bg-virgo-navy-600 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-virgo-navy-700 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-virgo-navy-600">
            <thead className="bg-virgo-navy-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Resource
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  IP Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-virgo-navy-700 divide-y divide-virgo-navy-600">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-virgo-navy-600">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {formatDateTime(log.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-virgo-teal-500" />
                      <div>
                        <div className="text-sm font-medium text-white">
                          {log.contact_name || 'Unknown'}
                        </div>
                        <div className="text-xs text-virgo-teal-400">
                          {log.contact_email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getActionIcon(log.action)}
                      <span className="text-sm text-white">
                        {getActionLabel(log.action)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-300">
                    {log.resource_name ? (
                      <div>
                        <div>{log.resource_name}</div>
                        {log.resource_type && (
                          <div className="text-xs text-virgo-teal-500">{log.resource_type}</div>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {log.ip_address || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {log.success ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900 text-green-200">
                        Success
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900 text-red-200">
                        Failed
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-virgo-teal-400">
                    {log.details ? (
                      <details className="cursor-pointer">
                        <summary className="text-virgo-teal-400 hover:text-virgo-teal-200">View</summary>
                        <pre className="mt-2 text-xs bg-virgo-navy-800 p-2 rounded max-w-xs overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </details>
                    ) : (
                      log.error_message || '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {logs.length === 0 && !loading && (
          <div className="text-center py-12">
            <Activity className="mx-auto h-12 w-12 text-virgo-teal-500" />
            <h3 className="mt-2 text-sm font-medium text-white">No activity found</h3>
            <p className="mt-1 text-sm text-virgo-teal-400">
              Client portal activity will appear here
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-virgo-teal-300">
            Showing {offset + 1} - {Math.min(offset + 50, total)} of {total} entries
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - 50))}
              disabled={offset === 0}
              className="px-4 py-2 bg-virgo-navy-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-virgo-navy-500"
            >
              Previous
            </button>
            <button
              onClick={() => setOffset(offset + 50)}
              disabled={offset + 50 >= total}
              className="px-4 py-2 bg-virgo-navy-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-virgo-navy-500"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );

  const renderSummaryTab = () => (
    <>
      {/* Period Selector */}
      <div className="mb-6 flex items-center gap-4">
        <label className="text-sm font-medium text-virgo-teal-200">Time Period:</label>
        <select
          value={summaryDays}
          onChange={(e) => setSummaryDays(parseInt(e.target.value))}
          className="px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
        >
          <option value="7">Last 7 days</option>
          <option value="14">Last 14 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <button
          onClick={loadSummary}
          className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {summary && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-900 rounded-lg">
                  <Activity className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-400">Total Actions</p>
                  <p className="text-2xl font-bold text-white">{summary.total_actions}</p>
                </div>
              </div>
            </div>

            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-900 rounded-lg">
                  <Users className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-400">Active Clients</p>
                  <p className="text-2xl font-bold text-white">{summary.active_clients}</p>
                </div>
              </div>
            </div>

            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-900 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-400">Failed Logins</p>
                  <p className="text-2xl font-bold text-white">{summary.failed_logins}</p>
                </div>
              </div>
            </div>

            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-900 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-400">Avg Daily Actions</p>
                  <p className="text-2xl font-bold text-white">
                    {Math.round(summary.total_actions / summary.period_days)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions by Type */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Actions by Type</h3>
              <div className="space-y-3">
                {Object.entries(summary.actions_by_type).map(([action, count]) => (
                  <div key={action} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getActionIcon(action)}
                      <span className="text-sm text-virgo-teal-200">{getActionLabel(action)}</span>
                    </div>
                    <span className="text-sm font-medium text-white">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Most Active Clients */}
            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Most Active Clients</h3>
              <div className="space-y-3">
                {summary.most_active_clients.map((client, idx) => (
                  <div key={client.contact_id || idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-virgo-teal-600 flex items-center justify-center text-xs text-white font-medium">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="text-sm text-white">{client.contact_name || 'Unknown'}</div>
                        <div className="text-xs text-virgo-teal-400">{client.contact_email}</div>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-virgo-teal-300">
                      {client.action_count} actions
                    </span>
                  </div>
                ))}
                {summary.most_active_clients.length === 0 && (
                  <p className="text-sm text-virgo-teal-400">No client activity in this period</p>
                )}
              </div>
            </div>
          </div>

          {/* Most Viewed Cases */}
          <div className="bg-virgo-navy-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Most Viewed Cases</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-virgo-navy-600">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Case</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Views</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-virgo-navy-600">
                  {summary.most_viewed_cases.map((c, idx) => (
                    <tr key={c.case_id || idx}>
                      <td className="px-4 py-3 text-sm text-white">{c.case_name || c.case_id}</td>
                      <td className="px-4 py-3 text-sm text-right text-virgo-teal-300">{c.view_count}</td>
                    </tr>
                  ))}
                  {summary.most_viewed_cases.length === 0 && (
                    <tr>
                      <td colSpan="2" className="px-4 py-3 text-sm text-virgo-teal-400 text-center">
                        No case views in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );

  const getTierColor = (tier) => {
    switch (tier) {
      case 'highly_engaged': return 'bg-green-900 text-green-200';
      case 'active': return 'bg-blue-900 text-blue-200';
      case 'low': return 'bg-yellow-900 text-yellow-200';
      case 'ghost': return 'bg-red-900 text-red-200';
      default: return 'bg-gray-900 text-gray-200';
    }
  };

  const renderEngagementTab = () => (
    <>
      {/* Period Selector */}
      <div className="mb-6 flex items-center gap-4">
        <label className="text-sm font-medium text-virgo-teal-200">Time Period:</label>
        <select
          value={engagementDays}
          onChange={(e) => setEngagementDays(parseInt(e.target.value))}
          className="px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
        >
          <option value="7">Last 7 days</option>
          <option value="14">Last 14 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <button
          onClick={loadEngagement}
          className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {engagementLoading ? (
        <div className="flex justify-center items-center h-32">
          <div className="text-virgo-teal-400">Loading engagement data...</div>
        </div>
      ) : engagement ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-900 rounded-lg">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-400">Portal Users</p>
                  <p className="text-2xl font-bold text-white">{engagement.total_portal_users}</p>
                </div>
              </div>
            </div>

            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-900 rounded-lg">
                  <LogIn className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-400">Total Logins</p>
                  <p className="text-2xl font-bold text-white">{engagement.total_logins}</p>
                </div>
              </div>
            </div>

            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-900 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-400">Client Messages</p>
                  <p className="text-2xl font-bold text-white">{engagement.total_messages}</p>
                </div>
              </div>
            </div>

            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-900 rounded-lg">
                  <DollarSign className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-400">Payments ({engagement.total_payments})</p>
                  <p className="text-2xl font-bold text-white">
                    ${(engagement.total_payment_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tier Distribution + Alerts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Engagement Tier Distribution */}
            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Engagement Tiers</h3>
              <div className="space-y-3">
                {[
                  { key: 'highly_engaged', label: 'Highly Engaged', color: 'bg-green-500' },
                  { key: 'active', label: 'Active', color: 'bg-blue-500' },
                  { key: 'low', label: 'Low Activity', color: 'bg-yellow-500' },
                  { key: 'ghost', label: 'Ghost', color: 'bg-red-500' },
                ].map(({ key, label, color }) => {
                  const count = engagement.tier_distribution[key] || 0;
                  const total = Object.values(engagement.tier_distribution).reduce((a, b) => a + b, 0) || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-virgo-teal-200">{label}</span>
                        <span className="text-sm font-medium text-white">{count} ({pct}%)</span>
                      </div>
                      <div className="w-full bg-virgo-navy-600 rounded-full h-2">
                        <div className={`${color} h-2 rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Alerts */}
            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Attention Needed</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-red-900/30 border border-red-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <span className="text-sm text-red-200">Ghost Clients</span>
                  </div>
                  <span className="text-lg font-bold text-red-300">{engagement.ghost_clients}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-900/30 border border-yellow-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm text-yellow-200">Stalled Onboarding</span>
                  </div>
                  <span className="text-lg font-bold text-yellow-300">{engagement.stalled_onboarding}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-virgo-navy-600 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-virgo-teal-400" />
                    <span className="text-sm text-virgo-teal-200">Doc Uploads This Period</span>
                  </div>
                  <span className="text-lg font-bold text-white">{engagement.total_uploads}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Login Trend */}
          {engagement.daily_login_trend && engagement.daily_login_trend.length > 0 && (
            <div className="bg-virgo-navy-700 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">Daily Login Trend</h3>
              <div className="flex items-end gap-1 h-32">
                {(() => {
                  const maxCount = Math.max(...engagement.daily_login_trend.map(d => d.count), 1);
                  return engagement.daily_login_trend.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                      <div
                        className="w-full bg-virgo-teal-500 rounded-t hover:bg-virgo-teal-400 transition-colors min-h-[2px]"
                        style={{ height: `${(d.count / maxCount) * 100}%` }}
                        title={`${d.date}: ${d.count} logins`}
                      />
                      {i % Math.max(1, Math.floor(engagement.daily_login_trend.length / 7)) === 0 && (
                        <span className="text-xs text-virgo-teal-500 transform -rotate-45 origin-top-left mt-1 whitespace-nowrap">
                          {d.date.slice(5)}
                        </span>
                      )}
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Ghost Clients Table */}
          {ghostClients.length > 0 && (
            <div className="bg-virgo-navy-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">
                Ghost Clients ({ghostClients.length})
                <span className="text-sm font-normal text-virgo-teal-400 ml-2">
                  No login in {ghostDays}+ days with active cases
                </span>
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-virgo-navy-600">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Client</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Email</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Active Cases</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Days Inactive</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Last Login</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-virgo-navy-600">
                    {ghostClients.map((g, idx) => (
                      <tr key={g.contact_id || idx} className="hover:bg-virgo-navy-600">
                        <td className="px-4 py-3 text-sm text-white font-medium">{g.contact_name || 'Unknown'}</td>
                        <td className="px-4 py-3 text-sm text-virgo-teal-300">{g.email || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right text-white">{g.active_cases}</td>
                        <td className="px-4 py-3 text-sm text-right">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            g.days_inactive > 60 ? 'bg-red-900 text-red-200' :
                            g.days_inactive > 30 ? 'bg-yellow-900 text-yellow-200' :
                            'bg-gray-900 text-gray-200'
                          }`}>
                            {g.days_inactive} days
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-virgo-teal-400">
                          {g.last_login ? new Date(g.last_login).toLocaleDateString() : 'Never'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <TrendingUp className="mx-auto h-12 w-12 text-virgo-teal-500" />
          <h3 className="mt-2 text-sm font-medium text-white">No engagement data</h3>
          <p className="mt-1 text-sm text-virgo-teal-400">
            Engagement scores are calculated daily at 2 AM
          </p>
        </div>
      )}
    </>
  );

  if (loading && !logs.length && !summary) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-virgo-teal-400">Loading portal activity...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-virgo-gold-400">Client Portal Activity</h1>
        <p className="text-virgo-teal-300 mt-1">Monitor all client portal activity and behavior</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-virgo-navy-600">
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'logs'
              ? 'text-virgo-gold-400 border-b-2 border-virgo-gold-400'
              : 'text-virgo-teal-300 hover:text-white'
          }`}
        >
          Activity Logs
        </button>
        <button
          onClick={() => setActiveTab('summary')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'summary'
              ? 'text-virgo-gold-400 border-b-2 border-virgo-gold-400'
              : 'text-virgo-teal-300 hover:text-white'
          }`}
        >
          Summary & Analytics
        </button>
        <button
          onClick={() => setActiveTab('engagement')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'engagement'
              ? 'text-virgo-gold-400 border-b-2 border-virgo-gold-400'
              : 'text-virgo-teal-300 hover:text-white'
          }`}
        >
          Engagement
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {activeTab === 'logs' && renderLogsTab()}
      {activeTab === 'summary' && renderSummaryTab()}
      {activeTab === 'engagement' && renderEngagementTab()}
    </div>
  );
}

export default PortalActivityAudit;
